import React, { useState } from 'react';
import {
    IoStar,
    IoStarHalf,
    IoStarOutline,
    IoThumbsUp,
    IoPerson,
    IoCalendar,
    IoCheckmarkCircle,
    IoWarning,
} from 'react-icons/io5';
import styles from '../AddProduct.module.css';

function ProductReviews({
    reviews,
    setReviews,
    averageRating,
    setAverageRating,
    totalReviews,
    setTotalReviews,
    errorFields,
    setErrorFields,
    handleFieldChange,
}) {
    const [newReview, setNewReview] = useState({
        customerName: '',
        rating: 5,
        title: '',
        comment: '',
        verified: false,
        helpful: 0,
        date: new Date().toISOString().split('T')[0],
    });
    const [showAddForm, setShowAddForm] = useState(false);

    const addReview = () => {
        if (
            newReview.customerName.trim() &&
            newReview.title.trim() &&
            newReview.comment.trim()
        ) {
            const review = {
                id: Date.now(),
                ...newReview,
                date: new Date().toISOString(),
                verified: true,
            };
            setReviews([...reviews, review]);

            // Update average rating
            const totalRating =
                reviews.reduce((sum, r) => sum + r.rating, 0) +
                newReview.rating;
            const newAverage = totalRating / (reviews.length + 1);
            setAverageRating(newAverage);
            setTotalReviews(reviews.length + 1);

            setNewReview({
                customerName: '',
                rating: 5,
                title: '',
                comment: '',
                verified: false,
                helpful: 0,
                date: new Date().toISOString().split('T')[0],
            });
            setShowAddForm(false);
        }
    };

    const removeReview = (index) => {
        const updatedReviews = reviews.filter((_, i) => i !== index);
        setReviews(updatedReviews);

        // Update average rating
        if (updatedReviews.length > 0) {
            const totalRating = updatedReviews.reduce(
                (sum, r) => sum + r.rating,
                0
            );
            const newAverage = totalRating / updatedReviews.length;
            setAverageRating(newAverage);
        } else {
            setAverageRating(0);
        }
        setTotalReviews(updatedReviews.length);
    };

    const renderStars = (rating, size = 'medium') => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 !== 0;

        for (let i = 0; i < 5; i++) {
            if (i < fullStars) {
                stars.push(
                    <IoStar
                        key={i}
                        className={`${styles['star']} ${
                            styles[`star-${size}`]
                        } ${styles['star-filled']}`}
                    />
                );
            } else if (i === fullStars && hasHalfStar) {
                stars.push(
                    <IoStarHalf
                        key={i}
                        className={`${styles['star']} ${
                            styles[`star-${size}`]
                        } ${styles['star-half']}`}
                    />
                );
            } else {
                stars.push(
                    <IoStarOutline
                        key={i}
                        className={`${styles['star']} ${
                            styles[`star-${size}`]
                        } ${styles['star-empty']}`}
                    />
                );
            }
        }
        return stars;
    };

    const getRatingText = (rating) => {
        if (rating >= 4.5) return 'Excellent';
        if (rating >= 3.5) return 'Good';
        if (rating >= 2.5) return 'Average';
        if (rating >= 1.5) return 'Poor';
        return 'Very Poor';
    };

    return (
        <div className={styles['AddProduct-card']}>
            <div className={styles['AddProduct-card-header']}>
                <div className={styles['AddProduct-card-title']}>
                    <IoStar className={styles['AddProduct-card-icon']} />
                    <span>Product Reviews & Ratings</span>
                </div>
                <div className={styles['AddProduct-card-subtitle']}>
                    Manage customer reviews and ratings for better SEO and trust
                </div>
            </div>

            <div className={styles['reviews-container']}>
                {/* Rating Summary */}
                <div className={styles['rating-summary']}>
                    <div className={styles['rating-overview']}>
                        <div className={styles['rating-score']}>
                            <span className={styles['rating-number']}>
                                {averageRating.toFixed(1)}
                            </span>
                            <div className={styles['rating-stars']}>
                                {renderStars(averageRating, 'large')}
                            </div>
                            <div className={styles['rating-text']}>
                                {getRatingText(averageRating)} ({totalReviews}{' '}
                                reviews)
                            </div>
                        </div>

                        <div className={styles['rating-breakdown']}>
                            {[5, 4, 3, 2, 1].map((star) => {
                                const count = reviews.filter(
                                    (r) => r.rating === star
                                ).length;
                                const percentage =
                                    totalReviews > 0
                                        ? (count / totalReviews) * 100
                                        : 0;
                                return (
                                    <div
                                        key={star}
                                        className={styles['rating-bar']}
                                    >
                                        <span
                                            className={styles['rating-label']}
                                        >
                                            {star}<IoStar style={{ verticalAlign: 'middle' }} />
                                        </span>
                                        <div
                                            className={
                                                styles['rating-progress']
                                            }
                                        >
                                            <div
                                                className={
                                                    styles['rating-fill']
                                                }
                                                style={{
                                                    width: `${percentage}%`,
                                                }}
                                            ></div>
                                        </div>
                                        <span
                                            className={styles['rating-count']}
                                        >
                                            {count}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Add Review Form */}
                <div className={styles['review-add-section']}>
                    <div className={styles['review-add-header']}>
                        <h4>Add New Review</h4>
                        <button
                            onClick={() => setShowAddForm(!showAddForm)}
                            className={styles['review-toggle-button']}
                        >
                            {showAddForm ? 'Cancel' : 'Add Review'}
                        </button>
                    </div>

                    {showAddForm && (
                        <div className={styles['review-add-form']}>
                            <div className={styles['review-form-row']}>
                                <div className={styles['review-input-group']}>
                                    <label className={styles['review-label']}>
                                        Customer Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={newReview.customerName}
                                        onChange={(e) =>
                                            setNewReview({
                                                ...newReview,
                                                customerName: e.target.value,
                                            })
                                        }
                                        placeholder="Enter customer name"
                                        className={styles['review-input']}
                                        maxLength={50}
                                    />
                                </div>

                                <div className={styles['review-input-group']}>
                                    <label className={styles['review-label']}>
                                        Rating *
                                    </label>
                                    <div className={styles['rating-selector']}>
                                        {[1, 2, 3, 4, 5].map((rating) => (
                                            <button
                                                key={rating}
                                                type="button"
                                                onClick={() =>
                                                    setNewReview({
                                                        ...newReview,
                                                        rating,
                                                    })
                                                }
                                                className={`${
                                                    styles['rating-button']
                                                } ${
                                                    newReview.rating >= rating
                                                        ? styles[
                                                              'rating-button-active'
                                                          ]
                                                        : ''
                                                }`}
                                            >
                                                <IoStar />
                                            </button>
                                        ))}
                                        <span
                                            className={styles['rating-value']}
                                        >
                                            {newReview.rating} star
                                            {newReview.rating !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className={styles['review-input-group']}>
                                <label className={styles['review-label']}>
                                    Review Title *
                                </label>
                                <input
                                    type="text"
                                    value={newReview.title}
                                    onChange={(e) =>
                                        setNewReview({
                                            ...newReview,
                                            title: e.target.value,
                                        })
                                    }
                                    placeholder="e.g., Great product, highly recommended!"
                                    className={styles['review-input']}
                                    maxLength={100}
                                />
                            </div>

                            <div className={styles['review-input-group']}>
                                <label className={styles['review-label']}>
                                    Review Comment *
                                </label>
                                <textarea
                                    value={newReview.comment}
                                    onChange={(e) =>
                                        setNewReview({
                                            ...newReview,
                                            comment: e.target.value,
                                        })
                                    }
                                    placeholder="Write detailed review about the product..."
                                    className={styles['review-textarea']}
                                    rows={4}
                                    maxLength={500}
                                />
                                <div className={styles['review-char-count']}>
                                    {newReview.comment.length}/500 characters
                                </div>
                            </div>

                            <div className={styles['review-form-actions']}>
                                <button
                                    onClick={addReview}
                                    disabled={
                                        !newReview.customerName.trim() ||
                                        !newReview.title.trim() ||
                                        !newReview.comment.trim()
                                    }
                                    className={styles['review-add-button']}
                                    type="button"
                                >
                                    <IoCheckmarkCircle />
                                    Add Review
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Reviews List */}
                {reviews.length > 0 && (
                    <div className={styles['reviews-list-section']}>
                        <div className={styles['reviews-list-header']}>
                            <h4>Customer Reviews ({reviews.length})</h4>
                            <p>Manage and moderate customer reviews</p>
                        </div>

                        <div className={styles['reviews-list']}>
                            {reviews.map((review, index) => (
                                <div
                                    key={review.id}
                                    className={styles['review-item']}
                                >
                                    <div className={styles['review-header']}>
                                        <div
                                            className={
                                                styles['review-customer']
                                            }
                                        >
                                            <div
                                                className={
                                                    styles['review-avatar']
                                                }
                                            >
                                                <IoPerson />
                                            </div>
                                            <div
                                                className={
                                                    styles['review-info']
                                                }
                                            >
                                                <div
                                                    className={
                                                        styles['review-name']
                                                    }
                                                >
                                                    {review.customerName}
                                                    {review.verified && (
                                                        <IoCheckmarkCircle
                                                            className={
                                                                styles[
                                                                    'verified-badge'
                                                                ]
                                                            }
                                                        />
                                                    )}
                                                </div>
                                                <div
                                                    className={
                                                        styles['review-meta']
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles[
                                                                'review-rating'
                                                            ]
                                                        }
                                                    >
                                                        {renderStars(
                                                            review.rating,
                                                            'small'
                                                        )}
                                                    </div>
                                                    <div
                                                        className={
                                                            styles[
                                                                'review-date'
                                                            ]
                                                        }
                                                    >
                                                        <IoCalendar />
                                                        {new Date(
                                                            review.date
                                                        ).toLocaleDateString()}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            className={styles['review-actions']}
                                        >
                                            <button
                                                onClick={() =>
                                                    removeReview(index)
                                                }
                                                className={
                                                    styles[
                                                        'review-remove-button'
                                                    ]
                                                }
                                                type="button"
                                                title="Remove Review"
                                            >
                                                <IoWarning />
                                            </button>
                                        </div>
                                    </div>

                                    <div className={styles['review-content']}>
                                        <h5 className={styles['review-title']}>
                                            {review.title}
                                        </h5>
                                        <p className={styles['review-comment']}>
                                            {review.comment}
                                        </p>

                                        <div className={styles['review-stats']}>
                                            <div
                                                className={
                                                    styles['review-helpful']
                                                }
                                            >
                                                <IoThumbsUp />
                                                <span>
                                                    {review.helpful} found
                                                    helpful
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* SEO Tips */}
                <div className={styles['reviews-tips']}>
                    <h4>Review SEO Benefits</h4>
                    <ul>
                        <li>
                            Rich snippets in search results with star ratings
                        </li>
                        <li>
                            Increased click-through rates from search results
                        </li>
                        <li>Better user engagement and trust signals</li>
                        <li>Fresh content for search engines to crawl</li>
                        <li>Long-tail keyword opportunities in reviews</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default ProductReviews;
